import React, { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import Icon from '../../components/AppIcon';
import { useToast } from '../../context/ToastContext';
import { fetchCustomers } from '../../lib/adminApi';
import { exportToCsv, downloadFilename } from '../../lib/csvExport';

const customerExportColumns = [
  { key: 'id', label: 'ID' },
  { key: 'full_name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'total_events_organized', label: 'Events' },
  { key: 'total_spent', label: 'Lifetime Spend' },
  { key: 'created_at', label: 'Joined' },
];

export default function CustomerManagementPage() {
  const toast = useToast();
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    async function loadCustomers() {
      setLoading(true);
      setError(null);

      try {
        const data = await fetchCustomers();
        if (isMounted) {
          setCustomers(data);
        }
      } catch (err) {
        console.error('Failed to load customers', err);
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to load customers');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadCustomers();

    return () => {
      isMounted = false;
    };
  }, []);

  const filteredCustomers = useMemo(() => {
    if (!searchTerm) {
      return customers;
    }
    return customers.filter((customer) =>
      customer.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [customers, searchTerm]);

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Customer Directory</h2>
          <p className="text-sm text-muted-foreground">
            Monitor growth, spend velocity, and engagement quality across the customer network.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => {
              const rows = filteredCustomers.map((c) => ({
                id: c.id,
                full_name: c.full_name ?? '',
                email: c.email ?? '',
                phone: c.phone ?? '',
                total_events_organized: c.total_events_organized ?? '',
                total_spent: c.total_spent ?? '',
                created_at: c.created_at ? format(new Date(c.created_at), 'yyyy-MM-dd') : '',
              }));
              if (!rows.length) {
                toast.info('No customers to export');
                return;
              }
              exportToCsv(rows, customerExportColumns, downloadFilename('customers'));
              toast.success(`Exported ${rows.length} customers`);
            }}
            className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm font-medium text-foreground shadow-sm hover:bg-muted"
          >
            <Icon name="Download" size={16} /> Export
          </button>
          <button className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm font-medium text-foreground shadow-sm hover:bg-muted">
            <Icon name="PieChart" size={16} /> Segment Builder
          </button>
        </div>
      </header>

      {error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="rounded-xl border border-border bg-card shadow-sm">
        <div className="flex flex-col gap-3 border-b border-border/80 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex w-full items-center gap-2 rounded-md border border-border bg-background px-3 py-2 sm:w-72">
            <Icon name="Search" size={16} className="text-muted-foreground" />
            <input
              type="search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search customers by name or email"
              className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>
          <button className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground shadow-sm hover:bg-primary/90">
            <Icon name="Filter" size={16} /> Advanced filters
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border text-sm">
            <thead className="bg-muted/30 text-muted-foreground">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Customer</th>
                <th className="px-4 py-3 text-left font-semibold">Email</th>
                <th className="px-4 py-3 text-left font-semibold">Phone</th>
                <th className="px-4 py-3 text-left font-semibold">Events</th>
                <th className="px-4 py-3 text-left font-semibold">Lifetime Spend</th>
                <th className="px-4 py-3 text-left font-semibold">Joined</th>
                <th className="px-4 py-3 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/80">
              {loading && (
                <tr>
                  <td colSpan={7} className="px-6 py-6 text-center text-muted-foreground">
                    Loading customers…
                  </td>
                </tr>
              )}

              {!loading && filteredCustomers.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-6 py-6 text-center text-muted-foreground">
                    No customers found. Try adjusting search or filters.
                  </td>
                </tr>
              )}

              {filteredCustomers.map((customer) => (
                <tr key={customer.id} className="hover:bg-muted/30">
                  <td className="px-4 py-3">
                    <div className="flex flex-col">
                      <span className="font-semibold text-foreground">{customer.full_name ?? 'Unnamed customer'}</span>
                      <span className="text-xs text-muted-foreground">ID: {customer.id.slice(0, 8)}…</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{customer.email ?? '—'}</td>
                  <td className="px-4 py-3 text-muted-foreground">{customer.phone ?? '—'}</td>
                  <td className="px-4 py-3 text-foreground">{Number(customer.total_events_organized ?? 0).toLocaleString()}</td>
                  <td className="px-4 py-3 font-semibold text-foreground">
                    R{Number(customer.total_spent ?? 0).toLocaleString(undefined, { minimumFractionDigits: 0 })}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {customer.created_at ? format(new Date(customer.created_at), 'dd MMM yyyy') : '—'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      <button className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1 text-xs font-semibold text-muted-foreground hover:bg-muted">
                        <Icon name="MessageCircle" size={14} /> Message
                      </button>
                      <button className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground hover:bg-primary/90">
                        <Icon name="ExternalLink" size={14} /> View profile
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
